/**
 * AddMeterReadingDrawer — three-tab drawer for adding meter readings.
 *
 *   1. Manual    — pick room/type/month, see previous reading, enter new value.
 *   2. OCR       — upload one meter photo, AI extracts the value, confirm/edit.
 *   3. Bulk      — upload many meter photos at once, OCR each, batch-save.
 *
 * Used from MeterReadingsManagement's header "Thêm chỉ số" button.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Icons, Drawer, DrawerBody, DrawerSection, DrawerFooter, Button,
} from '../../shared/ui';
import api from '../../utils/api';

const nowYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fieldCls =
  'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';

const labelCls = 'block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1';

export default function AddMeterReadingDrawer({ open, onClose }) {
  const { t } = useTranslation(['finance', 'common']);
  const [tab, setTab] = useState('manual');

  const TABS = [
    { key: 'manual', label: t('finance:meter.add.tab.manual'), icon: <Icons.Edit size={14} /> },
    { key: 'ocr',    label: t('finance:meter.add.tab.ocr'),    icon: <Icons.Camera size={14} /> },
    { key: 'bulk',   label: t('finance:meter.add.tab.bulk'),   icon: <Icons.Upload size={14} /> },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('finance:meter.add.title')}
      subtitle={t('finance:meter.add.subtitle')}
      width={480}
    >
      <DrawerBody className="p-0">
        {/* Tabs */}
        <div className="flex border-b border-stone-200">
          {TABS.map(tb => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={`flex-1 flex items-center justify-center gap-1 px-2 py-2.5 text-[12px] font-medium border-b-2 transition-colors -mb-px ${
                tab === tb.key
                  ? 'border-teal-600 text-teal-700 bg-teal-50/30'
                  : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}
            >
              {tb.icon}
              <span className="truncate">{tb.label}</span>
            </button>
          ))}
        </div>

        {tab === 'manual' && <ManualTab onClose={onClose} />}
        {tab === 'ocr'    && <OcrTab onClose={onClose} />}
        {tab === 'bulk'   && <BulkTab onClose={onClose} />}
      </DrawerBody>
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared metadata block — building / room / type / month
// ─────────────────────────────────────────────────────────────────────────────

function MetadataFields({ value, onChange, allowMultiRoom = false }) {
  const { t } = useTranslation('finance');
  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings-add-meter'],
    queryFn: () => api.get('/api/properties/buildings').then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.buildings ?? []);
    }),
    staleTime: 10 * 60 * 1000,
  });
  const { data: rooms = [] } = useQuery({
    queryKey: ['rooms-add-meter', value.building_id],
    queryFn: () => api.get(`/api/properties/buildings/${value.building_id}/rooms`).then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.rooms ?? []);
    }),
    enabled: !!value.building_id,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-3">
      <div>
        <label className={labelCls}>{t('meter.add.label.building')}</label>
        <select
          value={value.building_id || ''}
          onChange={e => onChange({ ...value, building_id: e.target.value || '', room_id: '' })}
          className={fieldCls}
        >
          <option value="">{t('meter.add.label.pickBuilding')}</option>
          {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {!allowMultiRoom && (
        <div>
          <label className={labelCls}>{t('meter.add.label.room')}</label>
          <select
            value={value.room_id || ''}
            onChange={e => onChange({ ...value, room_id: e.target.value || '' })}
            className={fieldCls}
            disabled={!value.building_id}
          >
            <option value="">{t('meter.add.label.pickRoom')}</option>
            {rooms.map(r => <option key={r.id} value={r.id}>{r.room_number}</option>)}
          </select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 min-w-0">
        <div>
          <label className={labelCls}>{t('meter.add.label.type')}</label>
          <select
            value={value.reading_type || 'electricity'}
            onChange={e => onChange({ ...value, reading_type: e.target.value })}
            className={fieldCls}
          >
            <option value="electricity">{t('meter.type.electricity')}</option>
            <option value="water">{t('meter.type.water')}</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>{t('meter.add.label.month')}</label>
          <input
            type="month"
            value={value.month || nowYM()}
            onChange={e => onChange({ ...value, month: e.target.value })}
            className={fieldCls}
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Manual tab
// ─────────────────────────────────────────────────────────────────────────────

function ManualTab({ onClose }) {
  const { t } = useTranslation(['finance', 'common']);
  const qc = useQueryClient();
  const [form, setForm] = useState({
    building_id: '',
    room_id: '',
    reading_type: 'electricity',
    month: nowYM(),
    reading_value: '',
    notes: '',
    is_base_reading: false,
  });

  // Lookup the previous verified reading for this room/type to anchor the form.
  const { data: previous } = useQuery({
    queryKey: ['previous-reading', form.room_id, form.reading_type, form.month],
    queryFn: () => api.get('/api/admin/meter-readings/previous', {
      params: { room_ids: form.room_id, reading_type: form.reading_type, month: form.month },
    }).then(r => {
      const entry = r.data?.[String(form.room_id)];
      return entry ? { previous_value: entry.previous_value, previous_month: entry.previous_month } : null;
    }).catch(() => null),
    enabled: !!form.room_id,
    staleTime: 30 * 1000,
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/meter-readings', {
      room_id: parseInt(form.room_id, 10),
      reading_type: form.reading_type,
      month: form.month,
      reading_value: parseFloat(form.reading_value),
      notes: form.notes || null,
      is_base_reading: !!form.is_base_reading,
    }).then(r => r.data),
    onSuccess: () => {
      toast.success(t('finance:meter.add.toast.saved'));
      qc.invalidateQueries({ queryKey: ['meter-readings'] });
      onClose();
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || e?.message || t('finance:meter.add.toast.failed');
      toast.error(detail);
    },
  });

  const previousValue = previous?.previous_value ?? previous?.reading_value ?? previous?.adjusted_reading ?? null;
  const newValueNum = parseFloat(form.reading_value);
  const usage = previousValue != null && !isNaN(newValueNum) ? newValueNum - previousValue : null;
  const canSubmit = form.room_id && form.reading_value && !isNaN(newValueNum);

  return (
    <div className="p-4 space-y-4">
      <MetadataFields value={form} onChange={setForm} />

      {/* Previous reading panel */}
      {form.room_id && (
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
            {t('finance:meter.add.label.previousReading')}
          </div>
          {previousValue != null ? (
            <div className="text-base font-semibold text-stone-900 num">
              {previousValue.toLocaleString()} {form.reading_type === 'water' ? 'm³' : 'kWh'}
            </div>
          ) : (
            <div className="text-[12px] italic text-stone-500">
              {t('finance:meter.add.label.noPrevious')}
            </div>
          )}
        </div>
      )}

      {/* New reading */}
      <div>
        <label className={labelCls}>{t('finance:meter.add.label.newReading')} *</label>
        <input
          type="number"
          step="0.01"
          value={form.reading_value}
          onChange={e => setForm({ ...form, reading_value: e.target.value })}
          placeholder="0"
          className={fieldCls}
        />
        {usage != null && (
          <div className={`mt-1 text-[12px] ${usage < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {usage < 0
              ? t('finance:meter.add.label.usageInvalid')
              : t('finance:meter.add.label.usageDelta', {
                  amount: usage.toLocaleString(),
                  unit: form.reading_type === 'water' ? 'm³' : 'kWh',
                })
            }
          </div>
        )}
      </div>

      <div>
        <label className={labelCls}>{t('finance:meter.add.label.notes')}</label>
        <textarea
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          rows={2}
          className={`${fieldCls} h-auto py-2 resize-y`}
        />
      </div>

      <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-stone-50 border border-stone-200">
        <input
          type="checkbox"
          checked={form.is_base_reading}
          onChange={e => setForm({ ...form, is_base_reading: e.target.checked })}
          className="mt-0.5 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
        />
        <span className="flex-1">
          <span className="block text-[13px] font-medium text-stone-800">
            {t('finance:meter.add.label.markAsBase')}
          </span>
          <span className="block text-[11px] text-stone-500 mt-0.5">
            {t('finance:meter.add.label.markAsBaseHint')}
          </span>
        </span>
      </label>

      <DrawerFooter>
        <Button kind="ghost" onClick={onClose}>{t('common:action.cancel')}</Button>
        <Button
          kind="primary"
          loading={create.isPending}
          disabled={!canSubmit || create.isPending}
          onClick={() => create.mutate()}
        >
          {t('finance:meter.add.action.save')}
        </Button>
      </DrawerFooter>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OCR tab — single photo
// ─────────────────────────────────────────────────────────────────────────────

function OcrTab({ onClose }) {
  const { t } = useTranslation(['finance', 'common']);
  const qc = useQueryClient();
  const [form, setForm] = useState({
    building_id: '',
    room_id: '',
    reading_type: 'electricity',
    month: nowYM(),
    reading_value: '',
    confidence: null,
    is_base_reading: false,
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [extracting, setExtracting] = useState(false);

  // Preview URL for the selected image (object URL, revoked on cleanup).
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const runOcr = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('meter_type', form.reading_type === 'water' ? 'water' : 'electric');
      const res = await api.post('/api/ocr/meter', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const value = res.data?.meter_value ?? res.data?.value ?? null;
      const conf  = res.data?.confidence ?? null;
      if (value != null) {
        setForm(prev => ({ ...prev, reading_value: String(value), confidence: conf }));
        toast.success(t('finance:meter.add.toast.ocrSuccess'));
      } else {
        toast.error(t('finance:meter.add.toast.ocrFailed'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('finance:meter.add.toast.ocrFailed'));
    } finally {
      setExtracting(false);
    }
  };

  const create = useMutation({
    mutationFn: async () => {
      const payload = {
        room_id: parseInt(form.room_id, 10),
        reading_type: form.reading_type,
        month: form.month,
        reading_value: parseFloat(form.reading_value),
        is_base_reading: !!form.is_base_reading,
      };
      const created = await api.post('/api/meter-readings', payload).then(r => r.data);
      // Attach the proof image to the new reading row.
      if (file && created?.id) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/api/admin/meter-readings/${created.id}/image`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      return created;
    },
    onSuccess: () => {
      toast.success(t('finance:meter.add.toast.saved'));
      qc.invalidateQueries({ queryKey: ['meter-readings'] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t('finance:meter.add.toast.failed')),
  });

  const canSubmit = form.room_id && form.reading_value && !isNaN(parseFloat(form.reading_value));

  return (
    <div className="p-4 space-y-4">
      <MetadataFields value={form} onChange={setForm} />

      {/* Image upload */}
      <div>
        <label className={labelCls}>{t('finance:meter.add.label.meterPhoto')} *</label>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={e => { setFile(e.target.files?.[0] || null); setForm(prev => ({ ...prev, reading_value: '', confidence: null })); }}
          className="block w-full text-[13px] text-stone-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-teal-50 file:text-teal-700 file:font-semibold file:cursor-pointer"
        />
        {preview && (
          <div className="mt-2 relative">
            <img src={preview} alt="meter" className="w-full rounded-lg border border-stone-200 max-h-48 object-contain bg-stone-50" />
          </div>
        )}
        {file && (
          <Button
            kind="secondary"
            size="sm"
            className="mt-2 w-full justify-center"
            loading={extracting}
            disabled={extracting}
            onClick={runOcr}
            iconLeft={<Icons.Sparkles size={14} />}
          >
            {extracting ? t('finance:meter.add.action.ocrRunning') : t('finance:meter.add.action.runOcr')}
          </Button>
        )}
      </div>

      {/* Extracted value */}
      <div>
        <label className={labelCls}>
          {t('finance:meter.add.label.detectedValue')} *
          {form.confidence != null && (
            <span className="ml-2 text-emerald-600 normal-case">
              ({Math.round(form.confidence * 100)}% {t('finance:meter.add.label.confidence')})
            </span>
          )}
        </label>
        <input
          type="number"
          step="0.01"
          value={form.reading_value}
          onChange={e => setForm({ ...form, reading_value: e.target.value })}
          className={fieldCls}
          placeholder={t('finance:meter.add.label.detectedPlaceholder')}
        />
      </div>

      <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-stone-50 border border-stone-200">
        <input
          type="checkbox"
          checked={form.is_base_reading}
          onChange={e => setForm({ ...form, is_base_reading: e.target.checked })}
          className="mt-0.5 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
        />
        <span className="flex-1">
          <span className="block text-[13px] font-medium text-stone-800">
            {t('finance:meter.add.label.markAsBase')}
          </span>
          <span className="block text-[11px] text-stone-500 mt-0.5">
            {t('finance:meter.add.label.markAsBaseHint')}
          </span>
        </span>
      </label>

      <DrawerFooter>
        <Button kind="ghost" onClick={onClose}>{t('common:action.cancel')}</Button>
        <Button
          kind="primary"
          loading={create.isPending}
          disabled={!canSubmit || create.isPending}
          onClick={() => create.mutate()}
        >
          {t('finance:meter.add.action.save')}
        </Button>
      </DrawerFooter>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk tab — multiple meter photos at once
// ─────────────────────────────────────────────────────────────────────────────

function BulkTab({ onClose }) {
  const { t } = useTranslation(['finance', 'common']);
  const qc = useQueryClient();
  const [mode, setMode] = useState('manual');  // 'manual' | 'ocr'
  const [form, setForm] = useState({
    building_id: '',
    reading_type: 'electricity',
    month: nowYM(),
    arrangement: 'horizontal',
  });
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState(null);  // array of {file, room_id?, reading_value?, status}
  const [processing, setProcessing] = useState(false);
  // Manual-bulk entries: keyed by room_id → { reading_value, is_base_reading }
  const [manualEntries, setManualEntries] = useState({});

  const { data: rooms = [] } = useQuery({
    queryKey: ['rooms-bulk', form.building_id],
    queryFn: () => api.get(`/api/properties/buildings/${form.building_id}/rooms`).then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.rooms ?? []);
    }),
    enabled: !!form.building_id,
    staleTime: 5 * 60 * 1000,
  });

  // Previous-readings map for the manual-bulk table: one batch request for all rooms.
  const { data: previousMap = {} } = useQuery({
    queryKey: ['bulk-previous', form.building_id, form.reading_type, form.month],
    queryFn: async () => {
      const roomIds = rooms.map(r => r.id).join(',');
      try {
        const res = await api.get('/api/admin/meter-readings/previous', {
          params: { room_ids: roomIds, reading_type: form.reading_type, month: form.month },
        });
        const data = res.data || {};
        // Response is { "room_id": { previous_value, ... } } keyed by string room id
        const map = {};
        rooms.forEach(r => {
          const entry = data[String(r.id)];
          map[r.id] = entry?.previous_value ?? null;
        });
        return map;
      } catch (_) {
        return Object.fromEntries(rooms.map(r => [r.id, null]));
      }
    },
    enabled: !!form.building_id && rooms.length > 0 && mode === 'manual',
    staleTime: 30 * 1000,
  });

  const processBulk = async () => {
    if (!files.length || !form.building_id) return;
    setProcessing(true);
    try {
      // The OCR endpoint accepts ONE image per call (containing multiple meters).
      // Call once per uploaded file and accumulate results.
      // Gateway returns: { readings: [...matched rooms], unmatched_meters: [...], stats: {...} }
      const allItems = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('building_id', String(form.building_id));
        fd.append('meter_type', form.reading_type === 'water' ? 'water' : 'electric');
        fd.append('arrangement', form.arrangement);
        const res = await api.post('/api/ocr/meters-multi', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const matched   = res.data?.readings        || [];
        const unmatched = res.data?.unmatched_meters || [];

        matched.forEach(r => allItems.push({
          file:          f,
          reading_value: r.meter_value ?? '',
          confidence:    r.confidence ?? null,
          room_id:       r.room_id ?? '',
          room_number:   r.room_number ?? '',
          unit:          r.unit ?? (form.reading_type === 'water' ? 'm³' : 'kWh'),
          status:        r.meter_value != null ? 'ok' : 'failed',
          is_base_reading: false,
        }));

        unmatched.forEach(r => allItems.push({
          file:          f,
          reading_value: r.meter_value ?? '',
          confidence:    r.confidence ?? null,
          room_id:       '',   // operator must assign
          room_number:   r.room_label ?? '',
          unit:          r.unit ?? (form.reading_type === 'water' ? 'm³' : 'kWh'),
          status:        r.meter_value != null ? 'unmatched' : 'failed',
          is_base_reading: false,
        }));
      }

      if (allItems.length === 0) {
        toast.error(t('finance:meter.add.toast.noMetersDetected', 'Không nhận diện được đồng hồ nào trong ảnh'));
      }
      setResults(allItems);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('finance:meter.add.toast.bulkFailed'));
    } finally {
      setProcessing(false);
    }
  };

  // OCR bulk save: iterate over the OCR results table.
  const saveAll = useMutation({
    mutationFn: async () => {
      const successful = results.filter(r => r.room_id && r.reading_value && r.status !== 'failed');
      for (const r of successful) {
        await api.post('/api/meter-readings', {
          room_id: parseInt(r.room_id, 10),
          reading_type: form.reading_type,
          month: form.month,
          reading_value: parseFloat(r.reading_value),
          is_base_reading: !!r.is_base_reading,
        });
      }
      return successful.length;
    },
    onSuccess: (count) => {
      toast.success(t('finance:meter.add.toast.bulkSaved', { count }));
      qc.invalidateQueries({ queryKey: ['meter-readings'] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t('finance:meter.add.toast.failed')),
  });

  // Manual bulk save: iterate over the manual-entry map.
  const saveManualAll = useMutation({
    mutationFn: async () => {
      const entries = Object.entries(manualEntries)
        .filter(([_, v]) => v.reading_value !== '' && v.reading_value != null && !isNaN(parseFloat(v.reading_value)));
      for (const [room_id, v] of entries) {
        await api.post('/api/meter-readings', {
          room_id: parseInt(room_id, 10),
          reading_type: form.reading_type,
          month: form.month,
          reading_value: parseFloat(v.reading_value),
          is_base_reading: !!v.is_base_reading,
        });
      }
      return entries.length;
    },
    onSuccess: (count) => {
      toast.success(t('finance:meter.add.toast.bulkSaved', { count }));
      qc.invalidateQueries({ queryKey: ['meter-readings'] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t('finance:meter.add.toast.failed')),
  });

  const updateManualEntry = (roomId, patch) => {
    setManualEntries(prev => ({
      ...prev,
      [roomId]: { reading_value: '', is_base_reading: false, ...prev[roomId], ...patch },
    }));
  };

  const manualEntriesFilled = Object.values(manualEntries).filter(
    v => v.reading_value !== '' && v.reading_value != null && !isNaN(parseFloat(v.reading_value))
  ).length;

  const unit = form.reading_type === 'water' ? 'm³' : 'kWh';

  return (
    <div className="p-4 space-y-4">
      {/* Mode switch: Manual table (default) vs OCR multi-photo */}
      <div className="inline-flex rounded-md border border-stone-200 bg-stone-50 p-0.5 text-xs">
        <button
          type="button"
          onClick={() => setMode('manual')}
          className={`px-3 py-1 rounded ${mode === 'manual' ? 'bg-white shadow-sm text-stone-900 font-semibold' : 'text-stone-500 hover:text-stone-700'}`}
        >
          {t('finance:meter.add.bulkMode.manual')}
        </button>
        <button
          type="button"
          onClick={() => setMode('ocr')}
          className={`px-3 py-1 rounded ${mode === 'ocr' ? 'bg-white shadow-sm text-stone-900 font-semibold' : 'text-stone-500 hover:text-stone-700'}`}
        >
          {t('finance:meter.add.bulkMode.ocr')}
        </button>
      </div>

      <MetadataFields value={form} onChange={setForm} allowMultiRoom />

      {/* ─── Manual bulk mode: table of all rooms in the building ─── */}
      {mode === 'manual' && (
        <>
          {!form.building_id ? (
            <div className="text-center text-[12px] text-stone-500 py-4 bg-stone-50 rounded-lg border border-dashed border-stone-200">
              {t('finance:meter.add.label.pickBuildingFirst')}
            </div>
          ) : rooms.length === 0 ? (
            <div className="text-center text-[12px] text-stone-500 py-4">
              {t('finance:meter.add.label.noRoomsInBuilding')}
            </div>
          ) : (
            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[60px_1fr_auto_auto] gap-2 px-2 py-1.5 bg-stone-50 border-b border-stone-200 text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                <span>{t('finance:meter.add.col.room')}</span>
                <span>{t('finance:meter.add.col.previous')}</span>
                <span>{t('finance:meter.add.col.newReading')}</span>
                <span title={t('finance:meter.add.label.markAsBase')}>{t('finance:meter.add.col.base')}</span>
              </div>
              <div className="divide-y divide-stone-100 max-h-80 overflow-y-auto">
                {rooms.map(rm => {
                  const prev = previousMap[rm.id];
                  const entry = manualEntries[rm.id] || { reading_value: '', is_base_reading: false };
                  const newNum = parseFloat(entry.reading_value);
                  const invalid = prev != null && !isNaN(newNum) && newNum < prev;
                  return (
                    <div key={rm.id} className="grid grid-cols-[60px_1fr_auto_auto] gap-2 px-2 py-1.5 items-center text-[12px]">
                      <span className="font-semibold text-stone-800">{rm.room_number}</span>
                      <span className={`num ${prev == null ? 'text-stone-400 italic' : 'text-stone-600'}`}>
                        {prev != null ? `${prev.toLocaleString()} ${unit}` : t('finance:meter.add.label.noPreviousShort')}
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        value={entry.reading_value}
                        onChange={e => updateManualEntry(rm.id, { reading_value: e.target.value })}
                        placeholder="0"
                        className={`h-7 px-2 rounded border w-24 text-right num ${invalid ? 'border-red-300 bg-red-50' : 'border-stone-200'}`}
                      />
                      <input
                        type="checkbox"
                        checked={!!entry.is_base_reading}
                        onChange={e => updateManualEntry(rm.id, { is_base_reading: e.target.checked })}
                        className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                        title={t('finance:meter.add.label.markAsBase')}
                      />
                    </div>
                  );
                })}
              </div>
              {manualEntriesFilled > 0 && (
                <div className="px-2 py-1.5 bg-teal-50 text-[11px] text-teal-700 font-medium border-t border-teal-100">
                  {t('finance:meter.add.label.filledCount', { filled: manualEntriesFilled, total: rooms.length })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ─── OCR bulk mode: photo upload + extraction ─── */}
      {mode === 'ocr' && (
        <>
          <div>
            <label className={labelCls}>{t('finance:meter.add.label.arrangement')}</label>
            <select
              value={form.arrangement}
              onChange={e => setForm({ ...form, arrangement: e.target.value })}
              className={fieldCls}
            >
              <option value="horizontal">{t('finance:meter.add.arrangement.horizontal')}</option>
              <option value="vertical">{t('finance:meter.add.arrangement.vertical')}</option>
              <option value="grid">{t('finance:meter.add.arrangement.grid')}</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>{t('finance:meter.add.label.photos')} *</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={e => { setFiles(Array.from(e.target.files || [])); setResults(null); }}
              className="block w-full text-[13px] text-stone-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-teal-50 file:text-teal-700 file:font-semibold file:cursor-pointer"
            />
            {files.length > 0 && (
              <div className="mt-2 text-[12px] text-stone-600">
                {t('finance:meter.add.label.filesSelected', { count: files.length })}
              </div>
            )}
          </div>

          {files.length > 0 && !results && (
            <Button
              kind="secondary"
              className="w-full justify-center"
              loading={processing}
              disabled={processing || !form.building_id}
              onClick={processBulk}
              iconLeft={<Icons.Sparkles size={14} />}
            >
              {processing ? t('finance:meter.add.action.bulkRunning') : t('finance:meter.add.action.runBulkOcr')}
            </Button>
          )}

          {/* Results review */}
          {results && results.length === 0 && (
            <div className="text-center text-[12px] text-amber-600 py-4 bg-amber-50 rounded-lg border border-amber-200">
              {t('finance:meter.add.toast.noMetersDetected', 'Không nhận diện được đồng hồ nào. Kiểm tra ảnh và thử lại.')}
            </div>
          )}

          {results && results.length > 0 && (() => {
            const matchedCount   = results.filter(r => r.status === 'ok').length;
            const unmatchedCount = results.filter(r => r.status === 'unmatched').length;
            const failedCount    = results.filter(r => r.status === 'failed').length;
            return (
            <>
              {/* Summary + legend */}
              <div className="flex flex-wrap items-center gap-3 text-[11px] px-1">
                {matchedCount > 0 && (
                  <span className="flex items-center gap-1.5 text-emerald-700">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                    {matchedCount} phòng đang thuê — gán tự động
                  </span>
                )}
                {unmatchedCount > 0 && (
                  <span className="flex items-center gap-1.5 text-amber-700">
                    <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                    {unmatchedCount} chưa gán — có thể phòng trống, vui lòng xem xét
                  </span>
                )}
                {failedCount > 0 && (
                  <span className="flex items-center gap-1.5 text-red-600">
                    <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                    {failedCount} không đọc được
                  </span>
                )}
              </div>

              <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 max-h-72 overflow-y-auto">
              <div className="grid grid-cols-[8px_1fr_88px_20px_32px] gap-1.5 px-2 py-1 bg-stone-50 border-b border-stone-100 text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                <span />
                <span>{t('finance:meter.add.col.room')}</span>
                <span>{t('finance:meter.add.col.newReading')}</span>
                <span title={t('finance:meter.add.label.markAsBase')}>{t('finance:meter.add.col.base')}</span>
                <span>%</span>
              </div>
              {results.map((r, i) => (
                <div key={i} className={`px-2 py-1.5 flex items-center gap-1.5 text-[12px] ${r.status === 'unmatched' ? 'bg-amber-50/60' : ''}`}>
                  <span className={`w-2 h-2 flex-shrink-0 rounded-full ${
                    r.status === 'ok' ? 'bg-emerald-500' :
                    r.status === 'unmatched' ? 'bg-amber-400' : 'bg-red-500'
                  }`} title={r.status === 'unmatched' ? 'Phòng trống hoặc không khớp — cần gán thủ công' : r.status} />
                  <select
                    value={r.room_id}
                    onChange={e => setResults(prev => prev.map((it, j) => j === i ? { ...it, room_id: e.target.value, status: e.target.value ? 'ok' : 'unmatched' } : it))}
                    className={`flex-1 min-w-0 h-7 text-[12px] px-1.5 rounded border bg-white ${
                      r.status === 'unmatched' ? 'border-amber-300' : 'border-stone-200'
                    }`}
                  >
                    <option value="">{r.room_number ? `${r.room_number} (trống?)` : '— gán phòng —'}</option>
                    {rooms.map(rr => <option key={rr.id} value={rr.id}>{rr.room_number}</option>)}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    value={r.reading_value}
                    onChange={e => setResults(prev => prev.map((it, j) => j === i ? { ...it, reading_value: e.target.value } : it))}
                    className="h-7 text-[12px] px-1.5 rounded border border-stone-200 w-[88px]"
                    placeholder={r.unit}
                  />
                  <input
                    type="checkbox"
                    checked={!!r.is_base_reading}
                    onChange={e => setResults(prev => prev.map((it, j) => j === i ? { ...it, is_base_reading: e.target.checked } : it))}
                    className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                    title={t('finance:meter.add.label.markAsBase')}
                  />
                  <span className="text-[10px] text-stone-400 w-8 text-right">
                    {r.confidence != null ? `${Math.round(r.confidence * 100)}%` : ''}
                  </span>
                </div>
              ))}
            </div>
            </>
            );
          })()}
        </>
      )}

      <DrawerFooter>
        <Button kind="ghost" onClick={onClose}>{t('common:action.cancel')}</Button>
        {mode === 'manual' ? (
          <Button
            kind="primary"
            loading={saveManualAll.isPending}
            disabled={manualEntriesFilled === 0 || saveManualAll.isPending}
            onClick={() => saveManualAll.mutate()}
          >
            {t('finance:meter.add.action.saveCount', { count: manualEntriesFilled })}
          </Button>
        ) : results ? (
          <Button
            kind="primary"
            loading={saveAll.isPending}
            disabled={!results.some(r => r.room_id && r.reading_value) || saveAll.isPending}
            onClick={() => saveAll.mutate()}
          >
            {t('finance:meter.add.action.saveAll')}
          </Button>
        ) : null}
      </DrawerFooter>
    </div>
  );
}
